import {
	IconCheckOutline18 as Check,
	IconCodeCompareOutline18 as CodeCompare,
	IconCopyOutline18 as Copy,
} from "nucleo-ui-outline-18"

import type { AiChatMessage } from "@productlane/lib/src/ai"
import { cn } from "@productlane/lib/src/cn"

import type { typographyVariantsType } from "../typography"
import { useCopiedFlag } from "../../hooks/useCopiedFlag"
import { AiAssistantIcon } from "../../icons/project/ai-assistant-icon"
import { Button } from "../button"
import { typographyVariants } from "../typography"

type RewriteTarget = NonNullable<AiChatMessage["contentRewrite"]>["target"]

interface ContentRewriteCardProps {
	rewrite: NonNullable<AiChatMessage["contentRewrite"]>
	onApply: () => void
	/** Opens the side-by-side review of this rewrite against the live document. */
	onPreview?: () => void
	applied?: boolean
	className?: string
}

const APPLY_LABEL: Record<RewriteTarget, string> = {
	composer: "Apply to composer",
	article: "Apply to article",
	changelog: "Apply to changelog",
}

// Match the editor each target applies into: the thread composer and email
// editor render "message", the article and changelog editors render "editor".
const PREVIEW_TYPOGRAPHY: Record<RewriteTarget, typographyVariantsType> = {
	composer: "message",
	article: "editor",
	changelog: "editor",
}

export const getRewritePreviewTypography = (target: RewriteTarget): string =>
	typographyVariants({ variant: PREVIEW_TYPOGRAPHY[target], mode: "invert" })

// The document scale is built for a full-width editor, so in a 300px sidebar it
// renders as oversized type with 38px image margins. This keeps the document's
// structure and steps every size and gap down to card size.
export const REWRITE_PREVIEW_SCALE =
	"text-[0.8125rem] " +
	"prose-h1:!text-[0.9375rem] prose-h1:!mt-3.5 prose-h1:!mb-1 " +
	"prose-h2:!text-[0.875rem] prose-h2:!mt-3 prose-h2:!mb-1 " +
	"prose-h3:!text-[0.8125rem] prose-h3:!mt-2.5 prose-h3:!mb-1 " +
	"prose-h4:!text-[0.8125rem] prose-h5:!text-[0.8125rem] prose-h6:!text-[0.8125rem] " +
	"[&_p]:!text-[0.8125rem] [&_p:not(:first-child)]:!mt-2 " +
	"[&_li]:!text-[0.8125rem] prose-ul:!my-2 prose-ol:!my-2 prose-li:!my-0.5 " +
	"prose-img:!my-2 prose-img:!rounded-md " +
	"prose-hr:!my-3 prose-pre:!my-2 prose-pre:!text-[0.6875rem] " +
	"prose-blockquote:!my-2 prose-blockquote:!px-2.5 prose-blockquote:!py-0.5 " +
	"[&_blockquote_p]:!text-[0.8125rem] " +
	"prose-code:!text-[0.75rem] prose-table:!text-[0.75rem]"

// Strip HTML to readable plain text for the clipboard.
const htmlToText = (html: string) => {
	if (typeof document === "undefined") return html
	const el = document.createElement("div")
	el.innerHTML = html
	return (el.innerText || el.textContent || "").trim()
}

// Renders a copilot content suggestion as a preview card with Copy + Apply
// actions. Apply replaces the current composer / article / changelog content so
// the user previews the change in place. The preview reuses the assistant
// prose styling so it reads like the rendered document.
export const ContentRewriteCard = ({
	rewrite,
	onApply,
	onPreview,
	applied = false,
	className,
}: ContentRewriteCardProps) => {
	const [copied, markCopied] = useCopiedFlag(1500)

	const handleCopy = () => {
		void navigator.clipboard.writeText(htmlToText(rewrite.html))
		markCopied()
	}

	return (
		<div
			className={cn(
				"-mx-2 mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-750",
				className,
			)}
		>
			{rewrite.title && (
				<div className="border-b border-gray-200 px-3.5 py-2 text-sm font-medium tracking-tight text-primary dark:border-gray-700/80">
					{rewrite.title}
				</div>
			)}
			<div className="relative">
				{/* Same typography as the editor Apply writes into; the chat prose
				    overrides would flatten it. */}
				<div
					className={cn(
						getRewritePreviewTypography(rewrite.target),
						REWRITE_PREVIEW_SCALE,
						"max-h-80 overflow-y-auto px-3.5 py-2.5 [&>*:first-child]:!mt-0 [&_a]:[overflow-wrap:anywhere]",
					)}
					dangerouslySetInnerHTML={{ __html: rewrite.html }}
				/>
				{/* Fade the bottom edge so it reads as "more below" instead of a
				    hard cut against the action row. */}
				<div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent dark:from-gray-750" />
			</div>
			<div className="flex items-center justify-between gap-1 px-3 py-2">
				<Button
					size="sm"
					variant="ghost"
					onClick={handleCopy}
					Icon={copied ? Check : Copy}
				>
					{copied ? "Copied" : "Copy"}
				</Button>
				<div className="flex items-center gap-1">
					{onPreview && (
						<Button
							size="sm"
							variant="ghost"
							onClick={onPreview}
							Icon={CodeCompare}
						>
							Preview
						</Button>
					)}
					<Button
						size="sm"
						variant="ghost"
						onClick={onApply}
						disabled={applied}
						Icon={applied ? Check : AiAssistantIcon}
					>
						{applied
							? "Applied"
							: onPreview
								? "Apply"
								: APPLY_LABEL[rewrite.target]}
					</Button>
				</div>
			</div>
		</div>
	)
}
